import React, { useContext, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import CustomModal from "./CustomModal";
import { getCards } from "../_slice/paymentMethodSlice";
import {
  questTypeCheckout,
  questTypeConfirmPurchase,
  removeFromCart,
  clearCart,
} from "../_slice/questTypePackageSlice";
import AppContext from "../context/AppContext";
import {
  formatPrice,
  getCardBrandColor,
  getCardBrandLabel,
  normalizeCard,
  toastHandler,
} from "../../utils/utils";

// Stripe card element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#9e2146",
    },
  },
};

// Stripe is loaded once per publishable key and reused across checkouts.
let stripeLoader = { key: null, promise: null };
const getStripePromise = (publicKey) => {
  const key = publicKey || process.env.REACT_APP_STRIPE_PUBLIC_KEY;
  if (!key) return null;
  if (stripeLoader.key !== key) {
    stripeLoader = { key, promise: loadStripe(key) };
  }
  return stripeLoader.promise;
};

// Status badge styling, shared with the marketplace rows
export const statusBadgeStyle = (variant) => {
  const palette = {
    included: { background: "#DCFCE7", color: "#166534" },
    owned: { background: "#DCFCE7", color: "#166534" },
    expiring: { background: "#FEF3C7", color: "#92400E" },
    expired: { background: "#FEE2E2", color: "#B91C1C" },
    available: { background: "#E5E7EB", color: "#4B5563" },
  };
  return {
    padding: "4px 12px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "600",
    display: "inline-block",
    textAlign: "center",
    whiteSpace: "nowrap",
    ...(palette[variant] || palette.available),
  };
};

// Value used for the "pay with a card not saved yet" option
const NEW_CARD = "new";

// Selectable card row inside the payment step
const savedCardRowStyle = (active) => ({
  border: `1px solid ${active ? "#004D26" : "#E5E7EB"}`,
  background: "#fff",
  borderRadius: "8px",
  padding: "10px 14px",
  cursor: "pointer",
});

// Payment Form Component
function PaymentForm({
  clientSecret,
  onPaymentSuccess,
  onCancel,
  amount,
  items,
  savedCards,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  // A saved payment method id, NEW_CARD, or null while the buyer has not
  // chosen yet — in which case the default saved card applies.
  const [chosenCard, setChosenCard] = useState(null);

  // Derived rather than stored, so the right radio is checked on the very
  // first render once the saved cards arrive.
  const defaultCardId = savedCards?.length
    ? (savedCards.find((card) => card.isDefault) || savedCards[0]).id
    : NEW_CARD;
  const selectedCard = chosenCard ?? defaultCardId;
  const usingSavedCard = selectedCard !== NEW_CARD;

  const handlePayment = async (e) => {
    e.preventDefault();
    // This checkout can sit inside another form (the Add Quest popup). The
    // modal is portalled, but React still bubbles submit up the component
    // tree, so without this Pay Now would also submit the host form.
    e.stopPropagation();

    if (!stripe || processing) {
      return;
    }
    if (!usingSavedCard && !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    // A saved card is confirmed by its payment method id; a new one by the
    // card element. confirmCardPayment drives 3DS either way.
    const paymentMethod = usingSavedCard
      ? selectedCard
      : { card: elements.getElement(CardElement) };

    const { error: paymentError, paymentIntent } =
      await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod,
      });

    if (paymentError) {
      // An invalid_request_error on a saved card is a configuration problem,
      // not a card problem: the PaymentIntent has to name the same customer
      // the card is attached to. Fall back to card entry so the buyer can
      // still complete the purchase instead of being stuck on a dead option.
      if (usingSavedCard && paymentError.type === "invalid_request_error") {
        setChosenCard(NEW_CARD);
        setError(
          "Your saved card cannot be used for this purchase. Please enter your card details to continue."
        );
      } else {
        setError(paymentError.message);
      }
      setProcessing(false);
    } else if (paymentIntent?.status === "succeeded") {
      onPaymentSuccess(paymentIntent.id);
    } else {
      setError(
        "Payment has not been completed. Please try again or use another card."
      );
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handlePayment}>
      {items?.length > 0 && (
        <div className="mb-3">
          {items.map((item) => (
            <div
              key={`payItem_${item.id}`}
              className="d-flex justify-content-between align-items-center mb-1"
            >
              <span>{item.name}</span>
              <span>{formatPrice(item.price)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mb-4">
        <p className="mb-3">
          {/* The charged amount always comes from the checkout API (in cents) */}
          <strong>Amount:</strong> {formatPrice((amount || 0) / 100)}
        </p>

        {savedCards?.length > 0 && (
          <>
            <label className="innerlable mb-2">Pay With</label>
            {savedCards.map((card) => (
              <label
                key={`savedCard_${card.id}`}
                htmlFor={`savedCard_${card.id}`}
                className="d-flex align-items-center mb-2"
                style={{ ...savedCardRowStyle(selectedCard === card.id) }}
              >
                <input
                  type="radio"
                  id={`savedCard_${card.id}`}
                  name="questTypePaymentCard"
                  className="mr-3"
                  checked={selectedCard === card.id}
                  onChange={() => setChosenCard(card.id)}
                  disabled={processing}
                />
                <span
                  style={{
                    minWidth: "80px",
                    fontWeight: "700",
                    fontSize: "13px",
                    color: getCardBrandColor(card.brand),
                  }}
                >
                  {getCardBrandLabel(card.brand)}
                </span>
                <span className="flex-grow-1" style={{ fontSize: "13px" }}>
                  ending in {card.last4 || "----"}
                  {card.expMonth && card.expYear
                    ? ` · ${String(card.expMonth).padStart(2, "0")}/${card.expYear}`
                    : ""}
                </span>
                {card.isDefault && (
                  <span style={statusBadgeStyle("included")}>Default</span>
                )}
              </label>
            ))}

            <label
              htmlFor="questTypeNewCard"
              className="d-flex align-items-center mb-3"
              style={{ ...savedCardRowStyle(selectedCard === NEW_CARD) }}
            >
              <input
                type="radio"
                id="questTypeNewCard"
                name="questTypePaymentCard"
                className="mr-3"
                checked={selectedCard === NEW_CARD}
                onChange={() => setChosenCard(NEW_CARD)}
                disabled={processing}
              />
              <span style={{ fontSize: "13px" }}>Use a new card</span>
            </label>
          </>
        )}

        {!usingSavedCard && (
          <>
            <label className="innerlable mb-2">Card Details</label>
            <div
              className="form-control"
              style={{ padding: "12px", height: "auto" }}
            >
              <CardElement options={cardElementOptions} />
            </div>
          </>
        )}
      </div>
      {error && <p className="text-danger mb-3">{error}</p>}
      <div className="d-flex justify-content-between align-items-center">
        <button
          type="submit"
          className="submit_btn"
          disabled={!stripe || processing || (!usingSavedCard && !elements)}
        >
          {processing ? "Processing..." : "Pay Now"}
        </button>
        <button
          type="button"
          className="cancleBtn"
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * The Quest Type cart, its checkout and the Stripe payment step.
 *
 * Renders a cart button plus the two modals behind it, so any screen that
 * lets a user add Quest Types can complete the purchase with the same cart
 * and the same Stripe integration.
 *
 * @param {Function} [props.onPurchased] - Called after access is granted,
 *   for the host screen to refresh whatever it shows.
 */
function QuestTypeCheckout({ onPurchased }) {
  const [cartModal, setCartModal] = useState({ show: false });
  const [paymentModal, setPaymentModal] = useState({ show: false });
  const [paymentData, setPaymentData] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);

  // Guards a second confirm-purchase call for the same PaymentIntent
  const confirmingIntentRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);

  const { cart, checkoutStatus, confirmStatus, organisationHasFreeAccess } =
    useSelector((state) => state.questTypePackage);
  const { cards } = useSelector((state) => state.paymentMethod);

  const savedCards = useMemo(
    () => (cards || []).map(normalizeCard).filter((card) => card.id),
    [cards]
  );

  // UI-only subtotal — the charged amount always comes from the checkout API
  const cartTotal = useMemo(
    () => cart.reduce((sum, item) => sum + (Number(item.price) || 0), 0),
    [cart]
  );

  const isBusy =
    checkoutStatus === "loading" ||
    confirmStatus === "loading" ||
    paymentModal.show;

  const handleCheckout = () => {
    if (!cart.length || isBusy) return;

    // Distinct IDs only. Default-bundle Quest Types are normally left out, but
    // during Organisation Free Access every Quest Type is granted the same way,
    // so that rule must not block them.
    const incidentTypeIds = [
      ...new Set(
        cart
          .filter((item) => organisationHasFreeAccess || !item.isDefault)
          .map((item) => item.id)
      ),
    ];

    if (!incidentTypeIds.length) {
      toastHandler(false, "Your cart has no quest types that require purchase.");
      return;
    }

    dispatch(questTypeCheckout({ incidentTypeIds, navigate, setLoader })).then(
      (response) => {
        if (!response?.type?.endsWith("/fulfilled")) return;

        const payload = response.payload || {};

        // Free checkout — access is already granted, so no Stripe and no
        // confirm-purchase. Covers both Organisation Free Access (it carries
        // free_access_until) and a legacy $0 cart; the slice clears the cart,
        // marks the marketplace stale and shows the right message for each.
        if (payload.is_free) {
          setCartModal({ show: false });
          onPurchased?.();
          return;
        }

        if (!payload.client_secret) {
          toastHandler(false, "Checkout could not be started. Please try again.");
          return;
        }

        const promise = getStripePromise(payload.public_key);
        if (!promise) {
          toastHandler(
            false,
            "Payment is unavailable right now. Please try again later."
          );
          return;
        }

        // Offer any card the organisation already saved, so a repeat buyer
        // does not have to retype their details.
        dispatch(getCards({ navigate, setLoader }));

        setStripePromise(promise);
        setPaymentData({
          clientSecret: payload.client_secret,
          paymentIntentId: payload.payment_intent_id,
          amount: payload.amount,
          items: payload.items || [],
        });
        setCartModal({ show: false });
        setPaymentModal({ show: true });
      }
    );
  };

  // Stripe confirmed the PaymentIntent — grant access via confirm-purchase
  const handlePaymentSuccess = (paymentIntentId) => {
    if (confirmingIntentRef.current === paymentIntentId) return;
    confirmingIntentRef.current = paymentIntentId;

    setPaymentModal({ show: false });

    dispatch(
      questTypeConfirmPurchase({ paymentIntentId, navigate, setLoader })
    ).then(() => {
      setPaymentData(null);
      confirmingIntentRef.current = null;
      onPurchased?.();
    });
  };

  // Payment cancelled — nothing was charged, keep the cart as it is
  const handlePaymentCancel = () => {
    setPaymentModal({ show: false });
    setPaymentData(null);
  };

  return (
    <>
      <button
        type="button"
        className="submit_btn"
        onClick={() => setCartModal({ show: true })}
        disabled={!cart.length}
        style={{ opacity: cart.length ? 1 : 0.6, whiteSpace: "nowrap" }}
      >
        Cart ({cart.length}) &middot;{" "}
        {organisationHasFreeAccess ? "Free" : formatPrice(cartTotal)}
      </button>

      {/* Cart Modal */}
      <CustomModal
        show={cartModal.show}
        onHide={() => setCartModal({ show: false })}
        heading="Your Cart"
        size="md"
        body={
          <div>
            {cart.length === 0 ? (
              <p className="text-center mb-4">Your cart is empty.</p>
            ) : (
              <>
                {cart.map((item) => (
                  <div
                    key={`cart_${item.id}`}
                    className="d-flex justify-content-between align-items-center mb-3"
                  >
                    <div className="d-flex align-items-center">
                      {item.image && (
                        <img
                          src={`${process.env.REACT_APP_MEDIA_URL_ORG}${item.image}`}
                          alt={item.name || ""}
                          className="mr-3"
                          style={{
                            width: "36px",
                            height: "36px",
                            objectFit: "contain",
                          }}
                        />
                      )}
                      <span>{item.name}</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="mr-3">{formatPrice(item.price)}</span>
                      <button
                        type="button"
                        className="action_btns"
                        onClick={() => dispatch(removeFromCart(item.id))}
                        disabled={isBusy}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                <hr />
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <strong>Total</strong>
                  <strong>
                    {organisationHasFreeAccess ? "Free" : formatPrice(cartTotal)}
                  </strong>
                </div>
                {organisationHasFreeAccess && (
                  <p className="bundle_note mb-4">
                    No payment is required during your organisation's
                    free-access period.
                  </p>
                )}
              </>
            )}
            <div className="d-flex justify-content-between align-items-center custom_bottom_padding">
              <button
                type="button"
                className="submit_btn"
                onClick={handleCheckout}
                disabled={!cart.length || isBusy}
                style={{ opacity: !cart.length || isBusy ? 0.6 : 1 }}
              >
                {checkoutStatus === "loading" ? "Processing..." : "Checkout"}
              </button>
              <button
                type="button"
                className="cancleBtn"
                style={{ whiteSpace: "nowrap" }}
                onClick={() => dispatch(clearCart())}
                disabled={!cart.length || isBusy}
              >
                Clear Cart
              </button>
            </div>
          </div>
        }
      />

      {/* Payment Modal */}
      <CustomModal
        show={paymentModal.show}
        onHide={handlePaymentCancel}
        heading="Complete Payment"
        size="md"
        body={
          stripePromise && paymentData ? (
            <Elements stripe={stripePromise}>
              <PaymentForm
                clientSecret={paymentData.clientSecret}
                amount={paymentData.amount}
                items={paymentData.items}
                savedCards={savedCards}
                onPaymentSuccess={handlePaymentSuccess}
                onCancel={handlePaymentCancel}
              />
            </Elements>
          ) : (
            <p>Loading payment form...</p>
          )
        }
      />
    </>
  );
}

export default QuestTypeCheckout;
